/**
 * MyLojic MUI Theme
 *
 * Main theme configuration that composes colors, typography, and component overrides.
 * Supports light and dark modes via createAppTheme(mode).
 *
 * Version History:
 * v2.92 - 2026-02-07 - Dark mode support with createAppTheme(mode)
 *
 * Usage in App.tsx:
 *   import { ThemeProvider } from '@mui/material/styles';
 *   import { createAppTheme } from './theme/theme';
 *
 *   const theme = createAppTheme('light');
 *   <ThemeProvider theme={theme}>
 *     <App />
 *   </ThemeProvider>
 */

import { createTheme } from '@mui/material/styles';
import {
  primary, secondary, accent,
  background, text, status, border,
  darkBackground, darkText, darkStatus, darkBorder,
} from './colors';
import { typography } from './typography';
import { getComponentOverrides } from './componentOverrides';

// Extend MUI palette with custom colors
declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
  }
  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
  }
  interface TypeBackground {
    card: string;
    sidebar: string;
  }
}

// v2.92 - Create theme for the given mode (light or dark)
export const createAppTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primary.main,
        light: primary.light,
        dark: primary.dark,
        contrastText: primary.contrastText,
      },
      secondary: {
        main: isDark ? '#4D7A99' : secondary.main,
        light: isDark ? '#6B99B8' : secondary.light,
        dark: isDark ? '#002244' : secondary.dark,
        contrastText: secondary.contrastText,
      },
      accent: {
        main: accent.main,
        light: accent.light,
        dark: accent.dark,
        contrastText: accent.contrastText,
      },
      text: {
        primary: isDark ? darkText.primary : text.primary,
        secondary: isDark ? darkText.secondary : text.secondary,
        disabled: isDark ? darkText.disabled : text.disabled,
      },
      background: {
        default: isDark ? darkBackground.default : background.default,
        paper: isDark ? darkBackground.paper : background.paper,
        card: isDark ? darkBackground.card : background.card,
        sidebar: isDark ? darkBackground.sidebar : background.sidebar,
      },
      error: {
        main: isDark ? darkStatus.error : status.error,
        light: isDark ? darkStatus.errorLight : status.errorLight,
      },
      warning: {
        main: isDark ? darkStatus.warning : status.warning,
        light: isDark ? darkStatus.warningLight : status.warningLight,
      },
      success: {
        main: isDark ? darkStatus.success : status.success,
        light: isDark ? darkStatus.successLight : status.successLight,
      },
      info: {
        main: isDark ? darkStatus.info : status.info,
        light: isDark ? darkStatus.infoLight : status.infoLight,
      },
      divider: isDark ? darkBorder.light : border.light,
    },
    typography,
    components: getComponentOverrides(mode),
    shape: {
      borderRadius: 8,
    },
    spacing: 8, // Base spacing unit (8px)
  });
};

// Default export for backward compatibility (light mode)
const theme = createAppTheme('light');
export default theme;
